import { VbzButton, VbzInputText } from '@smiles/voebiz-ui-kit';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { cnpj } from 'cpf-cnpj-validator';
import { isCNPJ } from 'validation-br';
import { mask as maskCnpj } from 'validation-br/dist/cnpj'
import { validate as validateEmail } from 'email-validator';
import ReCAPTCHA from 'react-google-recaptcha';
import queryString from 'query-string';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { IdentifierType, ValidIdentifier } from './types.d';
import { differenceIsLessThan } from '../../utils/time';
import PasswordFlow from './PasswordFlow';
import ChangePassword from '../ChangePassword';
import { VoeBizApi } from '../../services/services';
import { ModalDefaultMessage, PasswordResponse, MessageType, RecoverComponentMobile } from '../../components';
import { bgSideImage, logoHeaderMobileVoebiz, logoFooterGroupVoebiz, logoHeaderVoebiz } from '../../assets';

const RecoverPassword: React.FC = () => {
  const { t } = useTranslation('recover_password');
  const api = VoeBizApi();
  const ldFlags = useFlags();
  const urlParams = useMemo(() => queryString.parse(window.location.search), []);
  const [changePwFlow, setChangePwFlow] = useState(false);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [login, setLogin] = useState(() => {
    if (!urlParams.login_hint || typeof urlParams.login_hint !== 'string') {
      return '';
    }
    return urlParams.login_hint.toString();
  });
  const [loginType, setLoginType] = useState<IdentifierType>(IdentifierType.UNKNOWN);
  const [email, setEmail] = useState('');
  const [tokenSmiles, setTokenSmiles] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [changePasswordToken, setChangePasswordToken] = useState<string>('');
  const [systemError, setSystemError] = useState(false);
  const [tokenCaptcha, setTokenRecaptcha] = useState('');
  const [imageFooter, setImageFooter] = useState('');
  const [bannerTitle, setBannerTitle] = useState('');
  const [showPasswordResponse, setShowPasswordResponse] = useState<MessageType | undefined>(undefined);
  const [isValidInput, setIsValidInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  window.addEventListener('resize', () => {
    if (window.innerWidth < 769) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  });
  useEffect(() => {
    if (window.innerWidth < 769) {
      setIsMobile(true);
    }
  }, [isMobile]);
  const enableAlphanumericCnpj = ldFlags?.enableAlphanumericCnpj ?? false;
  const libFeatureAlphanumericCnpj = () => {
    if (enableAlphanumericCnpj) {
      return {
        isValid: isCNPJ,
        mask: maskCnpj
      }
    }

    return {
      isValid: cnpj.isValid,
      mask: cnpj.format
    }
  }
  const validateIdentifier = useCallback((value): ValidIdentifier => {
    const ret: ValidIdentifier = {
      isValid: false,
      type: IdentifierType.UNKNOWN,
      mask: undefined,
    };

    if (value) {
      const isnum = /^\d+$/.test(value);
      if (isnum && (value.length === 9 || value.length === 10)) {
        ret.type = IdentifierType.MEMBER;
        ret.isValid = true;
      } else if (value.indexOf('@') > 0) {
        ret.type = IdentifierType.EMAIL;
        ret.isValid = validateEmail(value);
      } else if (value.length === 14) {
        ret.type = IdentifierType.CNPJ;
        ret.isValid = libFeatureAlphanumericCnpj().isValid(value);
        if (ret.isValid) {
          ret.mask = libFeatureAlphanumericCnpj().mask(value);
        }
      }
    }

    return ret;
  }, []);

  
  const verifyLogin = async () => {
    setIsLoading(true);
    const finalLogin = loginType === IdentifierType.CNPJ ? login.replace(/[./-]+/g, '') : login;
    const responseData = await api.memberInfoReset(IdentifierType[loginType], finalLogin, tokenCaptcha);
    const { success: successMasked, response } = responseData;
    if (successMasked) {
      const { data, smilesToken } = response;
      setEmail(data.message);
      setTokenSmiles(smilesToken);
      setChangePwFlow(true);
    } else {
      setSystemError(true);
    }
    handleCaptchaError();
    setIsLoading(false);
  };
  const handleCaptcha = (captcha: string | null) => {
    if (captcha) {
      setIsCaptchaValid(true);
      setTokenRecaptcha(captcha);
    }
  };
  const handleCaptchaError = () => {
    setChangePwFlow(false);
    setIsCaptchaValid(false);
  };
  useEffect(() => {
    const token = window.location.search.split('token=')[1];
    if (token) {
      let isValid = false;
      try {
        const parsedJwt = jwtDecode<JwtPayload>(token);
        isValid = parsedJwt && parsedJwt.iat ? differenceIsLessThan(parsedJwt.iat * 1000, 24 * 60 * 60 * 1000) : false;
      } catch (e) {
        console.log(e);
      }
      if (!isValid) {
        setShowPasswordResponse(MessageType.FailureLink);
      } else {
        setChangePasswordToken(token);
      }
    }
  }, []);
  useEffect(() => {
    setImageFooter(logoFooterGroupVoebiz);
    setBannerTitle('');
  }, []);
  const getErrorMessage = useCallback((type: IdentifierType) => {
    const typeStr: string = IdentifierType[type];
    return t(`validate_error_${typeStr.toLowerCase()}`);
  }, []);
  const handleIdentifierValidation = useCallback(
    (str: string) => {
      let inputText = str;
      const strNoChars = inputText.replace(/[./-]+/g, '');
      if (!isNaN(Number(strNoChars)) || (strNoChars.length === 14 && !strNoChars.includes("@"))) {
        inputText = strNoChars;
      }
      const { isValid, type, mask } = validateIdentifier(inputText);
      const hasError = !isValid && type !== IdentifierType.UNKNOWN;
      setLoginType(type);
      setErrorMessage(hasError ? getErrorMessage(type) : '');
      setLogin(mask || inputText);
      setIsValidInput(isValid);
      return !hasError;
    },
    [getErrorMessage, validateIdentifier],
  );
  if (showPasswordResponse) {
    return <PasswordResponse message={showPasswordResponse} />;
  }
  if (changePasswordToken) {
    return <ChangePassword smilesToken={changePasswordToken} />;
  }
  return (
    <>
      <div className="voebiz-member-password-wrapper">
        <header>
          <div className="voebiz-logo">
            <img src={isMobile ? logoHeaderMobileVoebiz : logoHeaderVoebiz} />
          </div>
        </header>
        <div className="content">
          <aside style={{ backgroundImage: `url(${bgSideImage})` }}>
            <div className="aside-content">
              <div className="aside-content-description">
                <h3>Sua empresa vai mais {'\n'} longe com o VoeBiz</h3>
              </div>
            </div>
          </aside>
          <main>
            <div className="main-content">
              <h3>{t('title')}</h3>
              <p>{t('description')}</p>
              <VbzInputText
                textLabel={t('label')}
                placeholder={t('placeholder')}
                value={login}
                onChange={(e) => {
                  setIsValidInput(false);
                  setLogin(e.target.value);
                }}
                validate={handleIdentifierValidation}
                errorMessage={errorMessage}
                debouncedTime={700}
                type="text"
              />
              {!changePwFlow && !showSuccess && !systemError && (
                <ReCAPTCHA
                  sitekey={ldFlags.recaptchaSiteKey}
                  onChange={handleCaptcha}
                  onErrored={handleCaptchaError}
                  onExpired={handleCaptchaError}
                />
              )}
              <VbzButton
                text={t('continue_button')}
                onClick={verifyLogin}
                disabled={!isValidInput || (!urlParams.bpr && !isCaptchaValid)}
                isLoading={isLoading}
              />
              {isMobile && <RecoverComponentMobile buttonText={t('create_account_button')} imageFooter={imageFooter} />}
            </div>
          </main>
        </div>
        <footer>
          <h3>{bannerTitle}</h3>
        </footer>
      </div>
      <PasswordFlow
        open={changePwFlow}
        email={email}
        tokenSmiles={tokenSmiles}
        onClose={() => setChangePwFlow(false)}
        onFinish={() => {
          setChangePwFlow(false);
          setShowSuccess(true);
        }}
      />
      <ModalDefaultMessage
        isOpen={systemError}
        translate="modal_wrong_user_data"
        toggle={() => setSystemError(false)}
        onFinish={() => setSystemError(false)}
        backdrop
        hideSecondaryButton
      />
    </>
  );
};
export default RecoverPassword;
